/**
 * Focus management for the two overlay primitives — Step 140.
 *
 * `modal.tsx` and `sheet.tsx` set `role="dialog"` and nothing else: no
 * `aria-modal`, no focus trap, no focus restore. A keyboard user could Tab
 * straight out of a modal into the page behind it, and on closing was dropped
 * at the top of the document rather than back on the button they pressed.
 * The portal is public-facing for a UK charity, where the Equality Act's
 * reasonable-adjustment duty applies regardless of PSBAR.
 *
 * What this does, in order, while `open` is true:
 *  1. remembers the element that had focus;
 *  2. moves focus into the panel — the first focusable control, else the
 *     panel itself (which is given `tabIndex={-1}` for the purpose);
 *  3. keeps Tab and Shift+Tab inside the panel (wrapping at the ends);
 *  4. locks the page behind from scrolling;
 *  5. on close, restores focus to the remembered element if it still exists.
 *
 * A hook rather than a Radix dependency: the two primitives are deliberately
 * simple and nesting has not demanded a portal layer. If it ever does, this
 * is the file that goes.
 */
import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null,
  );
}

export function useDialogFocus(open: boolean, panelRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const previous = document.activeElement as HTMLElement | null;

    // Initial focus. Deferred a frame so the panel has laid out — a control
    // that is not yet painted refuses focus silently.
    const raf = requestAnimationFrame(() => {
      const first = focusables(panel)[0];
      (first ?? panel).focus({ preventScroll: true });
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      // Restore, but only to something still on the page — the trigger may
      // have been unmounted by whatever the dialog did.
      if (previous && document.contains(previous) && typeof previous.focus === 'function') {
        previous.focus({ preventScroll: true });
      }
    };
  }, [open, panelRef]);
}
